import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, Upload, X, CheckCircle2, FileText, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { track } from "../../lib/track";

const INDUSTRIES = ["Energy", "Oil & Gas", "Construction", "Manufacturing", "Engineering / EPC", "Government", "Healthcare", "Technology", "Education", "Facilities", "Small & Mid-Sized Business", "Other"];
const UNITS = ["Each", "Box", "Case", "Pallet", "Set", "Pair", "Feet", "Meters", "Gallons", "Liters", "Pounds", "Kilograms", "Other"];
const ALLOWED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "jpg", "jpeg", "png", "zip", "txt"];
const MAX_SIZE = 10 * 1024 * 1024;

export const REQUIREMENT_TYPES = ["Commercial Procurement", "Federal / Government Requirement", "Prime Contractor RFQ", "General Sourcing Request"];
export const FEDERAL_TYPE = "Federal / Government Requirement";
const TYPE_BY_PARAM = { federal: FEDERAL_TYPE, prime: "Prime Contractor RFQ", general: "General Sourcing Request", commercial: "Commercial Procurement" };
const FED_EMPTY = {
  agency: "", solicitation_number: "", nsn: "", niin: "", part_number: "", sku: "", cage_code: "", manufacturer: "", line_item: "",
  quantity: "", unit_of_issue: "", response_deadline: "", required_delivery_date: "", delivery_destination: "", set_aside: "",
  required_documentation: "", additional_requirements: "",
};
const FED_FIELDS = [
  ["agency", "Agency"], ["solicitation_number", "Solicitation Number"], ["nsn", "NSN"], ["niin", "NIIN"], ["part_number", "Part Number"], ["sku", "SKU"],
  ["cage_code", "CAGE Code"], ["manufacturer", "Manufacturer"], ["line_item", "Line Item"], ["quantity", "Quantity"], ["unit_of_issue", "Unit of Issue"],
  ["response_deadline", "Response Deadline", "date"], ["required_delivery_date", "Required Delivery Date", "date"],
  ["delivery_destination", "Delivery Destination"], ["set_aside", "Set-Aside Type, if applicable"],
];
const EMPTY = {
  first_name: "", last_name: "", company: "", job_title: "", email: "", phone: "",
  industry: "", product_service: "", quantity: "", unit: "", specifications: "",
  brand: "", part_number: "", delivery_location: "", required_date: "", budget: "",
  additional_requirements: "", requirement_type: "Commercial Procurement", website: "",
};

export function RFQForm({ compact = false }) {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [fed, setFed] = useState(FED_EMPTY);
  const fileInput = useRef(null);
  const { search } = useLocation();
  useEffect(() => {
    const t = TYPE_BY_PARAM[new URLSearchParams(search).get("type") || ""];
    if (t) setForm((f) => ({ ...f, requirement_type: t }));
  }, [search]);
  const isFederal = form.requirement_type === FEDERAL_TYPE;
  const handleFed = (k) => (e) => setFed((f) => ({ ...f, [k]: e.target.value }));

  const aiAutofill = async () => {
    const f = files[0];
    if (!f) { toast.error("Attach a document first, then use AI auto-fill."); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/rfq/extract", fd);
      setForm((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(data.fields || {})) {
          if (v && !String(prev[k] || "").trim()) next[k] = v;
        }
        return next;
      });
      toast.success("AI filled the form from your document — please review before submitting.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "AI extraction failed. Please fill the form manually.");
    } finally {
      setExtracting(false);
    }
  };

  const handle = (k) => (e) => {
    track("rfq_started", {}, true);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const addFiles = (list) => {
    const next = [...files];
    for (const f of list) {
      const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
      if (!ALLOWED_EXT.includes(ext)) { toast.error(`${f.name}: file type not allowed`); continue; }
      if (f.size > MAX_SIZE) { toast.error(`${f.name}: exceeds 10MB limit`); continue; }
      if (next.length >= 5) { toast.error("Maximum 5 files"); break; }
      next.push(f);
    }
    setFiles(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    let payload = { ...form };
    if (isFederal) {
      const summary = [fed.nsn && `NSN ${fed.nsn}`, fed.part_number && `P/N ${fed.part_number}`, fed.manufacturer].filter(Boolean).join(" · ");
      if (payload.product_service.trim().length < 2 && summary) payload.product_service = summary;
      if (!payload.quantity && fed.quantity) payload.quantity = fed.quantity;
      if (!payload.unit && fed.unit_of_issue) payload.unit = fed.unit_of_issue;
      if (!payload.part_number && fed.part_number) payload.part_number = fed.part_number;
      if (!payload.brand && fed.manufacturer) payload.brand = fed.manufacturer;
      if (!payload.delivery_location && fed.delivery_destination) payload.delivery_location = fed.delivery_destination;
      if (!payload.required_date && fed.required_delivery_date) payload.required_date = fed.required_delivery_date;
      if (!payload.industry) payload.industry = "Government";
      payload.federal = fed;
    } else if (form.requirement_type === "Prime Contractor RFQ") {
      payload.federal = { ...FED_EMPTY, part_number: form.part_number, manufacturer: form.brand, quantity: form.quantity, unit_of_issue: form.unit,
        required_delivery_date: form.required_date, delivery_destination: form.delivery_location };
    }
    if (!payload.first_name.trim() || !payload.last_name.trim() || !payload.email.trim() || payload.product_service.trim().length < 2) {
      toast.error(isFederal ? "Please complete your name, email, and the NSN, part number or item description." : "Please complete your name, email, and what you need sourced.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/rfq", payload);
      let uploaded = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/rfq/${data.id}/documents`, fd);
          uploaded += 1;
        } catch {
          toast.error(`Upload failed for ${f.name}`);
        }
      }
      setResult({ rfq_number: data.rfq_number, uploaded, total: files.length, federal: isFederal ? { ...fed } : null, submitted_at: new Date() });
      track("rfq_submitted", { rfq_number: data.rfq_number, type: isFederal ? "federal" : "standard" });
      setForm({ ...EMPTY, requirement_type: form.requirement_type });
      setFed(FED_EMPTY);
      setFiles([]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again or call 281-901-9914.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.federal) {
    const f = result.federal;
    const rows = [["RFQ Number", result.rfq_number], ["Submission Date", result.submitted_at.toLocaleDateString("en-US", { dateStyle: "long" })],
      ["Solicitation Number", f.solicitation_number], ["NSN / Part Number", [f.nsn, f.part_number].filter(Boolean).join(" / ")], ["Status", "Received"]];
    return (
      <div data-testid="rfq-federal-success-panel" className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[var(--pg-accent)]/10 text-[var(--pg-accent)] flex items-center justify-center"><CheckCircle2 size={32} /></div>
          <h2 className="mt-5 font-[Poppins] font-extrabold text-[24px] text-[var(--pg-navy)]">Federal Requirement Received</h2>
          <p className="mt-3 text-[14.5px] text-[var(--pg-ink-soft)] max-w-xl mx-auto">
            Thank you. PGPS has received your federal procurement requirement. Our sourcing team will review the solicitation and requirement information and begin the appropriate supplier research.
            {result.total > 0 && ` ${result.uploaded} of ${result.total} document(s) attached.`}
          </p>
        </div>
        <dl className="mt-7 max-w-lg mx-auto border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="rfq-federal-summary">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 px-5 py-3 text-[14px]">
              <dt className="text-[var(--pg-ink-soft)]">{k}</dt>
              <dd className="font-[Poppins] font-semibold text-[var(--pg-navy)] text-right" data-testid={`rfq-federal-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{v || "—"}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-7 flex flex-wrap gap-3 justify-center">
          <button data-testid="rfq-submit-another-button" onClick={() => setResult(null)} className="btn-primary">Submit Another Requirement</button>
          <a href="/portal" data-testid="rfq-track-portal-link" className="inline-flex items-center gap-1.5 text-[13.5px] font-[Poppins] font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)] px-4 py-3">Track it in the Client Portal →</a>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div data-testid="rfq-success-panel" className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[var(--pg-accent)]/10 text-[var(--pg-accent)] flex items-center justify-center">
          <CheckCircle2 size={32} />
        </div>
        <div className="mt-5 font-[Poppins] font-bold text-[13px] tracking-[0.2em] uppercase text-[var(--pg-accent)]">RFQ Received</div>
        <div className="mt-3 inline-block bg-[var(--pg-bg)] border border-slate-200 rounded-xl px-6 py-3">
          <span className="font-[Poppins] font-extrabold text-[22px] md:text-[26px] text-[var(--pg-navy)]" data-testid="rfq-reference-number">
            RFQ #: {result.rfq_number}
          </span>
        </div>
        <p className="mt-4 font-[Poppins] font-semibold text-[16px] text-[var(--pg-navy)]">
          Your procurement request has been received successfully.
        </p>
        <p className="mt-2 text-[14.5px] text-[var(--pg-ink-soft)] max-w-lg mx-auto">
          A Patrick Global procurement specialist will review your requirement and respond within one business day.
          {result.total > 0 && ` ${result.uploaded} of ${result.total} document(s) attached.`}
        </p>
        <p className="mt-3 text-[12.5px] text-slate-500 max-w-md mx-auto">
          A confirmation email with this RFQ number is on its way. Please reference it in any correspondence.
        </p>
        <div className="mt-7 flex flex-wrap gap-3 justify-center">
          <button data-testid="rfq-submit-another-button" onClick={() => setResult(null)} className="btn-primary">
            Submit Another RFQ
          </button>
          <a href="/portal" data-testid="rfq-track-portal-link"
            className="inline-flex items-center gap-1.5 text-[13.5px] font-[Poppins] font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)] px-4 py-3 transition-colors">
            Track it in the Client Portal →
          </a>
        </div>
      </div>
    );
  }

  return (
    <form data-testid="rfq-form" onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 text-left">
      <input type="text" value={form.website} onChange={handle("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="mb-5">
        <Field label="What type of requirement are you submitting? *" id="rfq-requirement-type">
          <select id="rfq-requirement-type" data-testid="rfq-requirement-type-select" className="pg-input" value={form.requirement_type} onChange={handle("requirement_type")}>
            {REQUIREMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      {isFederal && (
        <fieldset data-testid="rfq-federal-fields" className="mb-6 border border-[var(--pg-accent)]/40 bg-[#f0f7ff] rounded-2xl p-5">
          <legend className="px-2 font-[Poppins] font-bold text-[12px] tracking-[0.18em] uppercase text-[var(--pg-accent)]">Federal Requirement Fields</legend>
          <div className="grid sm:grid-cols-2 gap-4">
            {FED_FIELDS.map(([k, label, type]) => (
              <Field key={k} label={label} id={`rfq-fed-${k}`}>
                <input id={`rfq-fed-${k}`} data-testid={`rfq-fed-${k}-input`} type={type || "text"} className="pg-input" value={fed[k]} onChange={handleFed(k)} />
              </Field>
            ))}
            <div className="sm:col-span-2">
              <Field label="Required Documentation" id="rfq-fed-required_documentation">
                <textarea id="rfq-fed-required_documentation" data-testid="rfq-fed-required_documentation-input" rows={2} className="pg-input resize-y" value={fed.required_documentation} onChange={handleFed("required_documentation")} placeholder="e.g. Certificate of conformance, traceability, packaging/marking, country of origin" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Additional Requirements" id="rfq-fed-additional_requirements">
                <textarea id="rfq-fed-additional_requirements" data-testid="rfq-fed-additional_requirements-input" rows={2} className="pg-input resize-y" value={fed.additional_requirements} onChange={handleFed("additional_requirements")} />
              </Field>
            </div>
          </div>
        </fieldset>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="First Name *" id="rfq-first-name"><input data-testid="rfq-first-name-input" className="pg-input" value={form.first_name} onChange={handle("first_name")} required /></Field>
        <Field label="Last Name *" id="rfq-last-name"><input data-testid="rfq-last-name-input" className="pg-input" value={form.last_name} onChange={handle("last_name")} required /></Field>
        <Field label="Company" id="rfq-company"><input data-testid="rfq-company-input" className="pg-input" value={form.company} onChange={handle("company")} /></Field>
        <Field label="Job Title" id="rfq-job-title"><input data-testid="rfq-job-title-input" className="pg-input" value={form.job_title} onChange={handle("job_title")} /></Field>
        <Field label="Email *" id="rfq-email"><input data-testid="rfq-email-input" type="email" className="pg-input" value={form.email} onChange={handle("email")} required /></Field>
        <Field label="Phone" id="rfq-phone"><input data-testid="rfq-phone-input" className="pg-input" value={form.phone} onChange={handle("phone")} /></Field>
        <Field label="Industry" id="rfq-industry">
          <select data-testid="rfq-industry-select" className="pg-input" value={form.industry} onChange={handle("industry")}>
            <option value="">Select industry...</option>
            {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Quantity" id="rfq-quantity"><input data-testid="rfq-quantity-input" className="pg-input" value={form.quantity} onChange={handle("quantity")} placeholder="e.g. 500" /></Field>
          <Field label="Unit" id="rfq-unit">
            <select data-testid="rfq-unit-select" className="pg-input" value={form.unit} onChange={handle("unit")}>
              <option value="">Unit...</option>
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label={isFederal ? "Item Description (optional if NSN / Part Number given above)" : "Product / Equipment / Service Needed *"} id="rfq-product">
            <input data-testid="rfq-product-input" className="pg-input" value={form.product_service} onChange={handle("product_service")} placeholder="e.g. 4-inch gate valves, safety glasses, network switches..." required={!isFederal} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Product Specifications" id="rfq-specifications">
            <textarea data-testid="rfq-specifications-input" rows={compact ? 3 : 4} className="pg-input resize-y" value={form.specifications} onChange={handle("specifications")} placeholder="Material, dimensions, standards, ratings, or any technical detail..." />
          </Field>
        </div>
        <Field label="Preferred Brand / Manufacturer" id="rfq-brand"><input data-testid="rfq-brand-input" className="pg-input" value={form.brand} onChange={handle("brand")} /></Field>
        <Field label="Part / SKU Number" id="rfq-part-number"><input data-testid="rfq-part-number-input" className="pg-input" value={form.part_number} onChange={handle("part_number")} /></Field>
        <Field label="Delivery Location" id="rfq-delivery-location"><input data-testid="rfq-delivery-location-input" className="pg-input" value={form.delivery_location} onChange={handle("delivery_location")} placeholder="City, State / Country" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Required Date" id="rfq-required-date"><input data-testid="rfq-required-date-input" type="date" className="pg-input" value={form.required_date} onChange={handle("required_date")} /></Field>
          <Field label="Budget (optional)" id="rfq-budget"><input data-testid="rfq-budget-input" className="pg-input" value={form.budget} onChange={handle("budget")} placeholder="$" /></Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Additional Requirements" id="rfq-additional">
            <textarea data-testid="rfq-additional-input" rows={2} className="pg-input resize-y" value={form.additional_requirements} onChange={handle("additional_requirements")} placeholder="Certifications, packaging, compliance, or anything else we should know..." />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <label className="pg-label">Attach RFQ, Purchase List, Specs, or Drawings (PDF, DOC, XLS, CSV, JPG, PNG, ZIP · max 10MB each)</label>
          <input ref={fileInput} type="file" multiple className="hidden" data-testid="rfq-file-input"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.zip,.txt"
            onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
          <button type="button" data-testid="rfq-file-upload-button" onClick={() => fileInput.current?.click()}
            className="mt-1 w-full border-2 border-dashed border-slate-300 hover:border-[var(--pg-accent)] rounded-xl py-5 flex flex-col items-center gap-2 text-[var(--pg-ink-soft)] hover:text-[var(--pg-accent)] transition-colors">
            <Upload size={22} />
            <span className="text-[13.5px] font-[Poppins] font-medium">Click to upload files (up to 5)</span>
          </button>
          {files.length > 0 && (
            <>
              <button type="button" data-testid="rfq-ai-autofill-button" onClick={aiAutofill} disabled={extracting}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 font-[Poppins] font-semibold text-[13.5px] bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)] transition-colors disabled:opacity-70">
                {extracting ? <><Loader2 className="animate-spin" size={16} /> Reading your document with AI...</> : <><Sparkles size={16} /> Auto-Fill Form from Document (AI)</>}
              </button>
              <ul className="mt-3 space-y-2" data-testid="rfq-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center justify-between bg-[var(--pg-bg)] rounded-lg px-3 py-2 text-[13.5px]">
                  <span className="flex items-center gap-2 truncate"><FileText size={15} className="text-[var(--pg-accent)] flex-shrink-0" />{f.name} <span className="text-[var(--pg-ink-soft)]">({(f.size / 1024 / 1024).toFixed(1)}MB)</span></span>
                  <button type="button" data-testid={`rfq-file-remove-${i}`} aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500 p-1"><X size={15} /></button>
                </li>
              ))}
              </ul>
            </>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-[12.5px] text-[var(--pg-ink-soft)] max-w-md">
          By submitting, you agree to be contacted by Patrick Global regarding your requirement. We do not share your details.
        </p>
        <button data-testid="rfq-submit-button" type="submit" disabled={submitting} className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
          {submitting ? <><Loader2 className="animate-spin" size={18} /> Submitting...</> : <>Submit RFQ <Send size={16} /></>}
        </button>
      </div>
    </form>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <label className="pg-label" htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}
